import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, MessageSquare, Mail, MoreHorizontal, Sparkles, Send, ExternalLink, Paperclip, Reply } from 'lucide-react';
type ThreadType = 'sms' | 'email';
type Thread = {
  id: number;
  type: ThreadType;
  consumer: string;
  account: string;
  preview: string;
  time: string;
  unread: number;
  subject?: string;
  messages: {
    direction: 'in' | 'out';
    text: string;
    time: string;
    from?: string;
    to?: string;
    system?: boolean;
  }[];
};
const mockThreads: Thread[] = [{
  id: 1,
  type: 'sms',
  consumer: 'Lissette Berpan',
  account: '102863517',
  preview: 'Can I pay this on Friday instead?',
  time: '10:42 AM',
  unread: 1,
  messages: [{
    direction: 'out',
    text: 'Hi Lissette, this is Compass Recovery Group. Your account balance is $4,244.12. We have a settlement offer available. Reply YES to review.',
    time: 'Yesterday, 2:15 PM',
    system: true
  }, {
    direction: 'in',
    text: 'YES',
    time: 'Yesterday, 2:40 PM'
  }, {
    direction: 'out',
    text: 'Great. You can view the offer and set up payment securely here: compassrecoverygroup.com/portal/ref-99281',
    time: 'Yesterday, 2:41 PM',
    system: true
  }, {
    direction: 'in',
    text: 'Can I pay this on Friday instead?',
    time: 'Today, 10:42 AM'
  }]
}, {
  id: 2,
  type: 'email',
  consumer: 'Michael Chang',
  account: '102863518',
  preview: 'Re: Settlement Offer enclosed',
  time: 'Yesterday',
  unread: 0,
  subject: 'Re: Settlement Offer enclosed',
  messages: [{
    direction: 'out',
    from: 'Compass Recovery Group <noreply@compassrecoverygroup.com>',
    to: 'mchang92@gmail.com',
    text: 'Hi Michael,\n\nPlease find your settlement offer details enclosed. Your current balance is $1,250.00 and we are pleased to offer a payment plan of 6 monthly installments at $208.\n\nReview and confirm here: compassrecoverygroup.com/portal/ref-83120\n\nBest,\nCompass Recovery Group',
    time: '3 days ago, 9:14 AM'
  }, {
    direction: 'in',
    from: 'mchang92@gmail.com',
    to: 'noreply@compassrecoverygroup.com',
    text: 'Thanks. I reviewed the payment plan and would like to confirm. Can you send a receipt after the first payment goes through?',
    time: 'Yesterday, 4:02 PM'
  }, {
    direction: 'out',
    from: 'Compass Recovery Group <noreply@compassrecoverygroup.com>',
    to: 'mchang92@gmail.com',
    text: 'Absolutely, Michael. A receipt will be emailed to you immediately after each scheduled payment. Your first installment is set for July 1, 2026.\n\nThank you,\nCompass Recovery Group',
    time: 'Yesterday, 4:38 PM'
  }]
}, {
  id: 3,
  type: 'sms',
  consumer: 'David Smith',
  account: '102863519',
  preview: 'Yes, that works.',
  time: 'Jun 12',
  unread: 0,
  messages: [{
    direction: 'out',
    text: 'Hi David, we have a settlement option available for your account. Would you like to discuss?',
    time: 'Jun 11, 1:00 PM',
    system: true
  }, {
    direction: 'in',
    text: 'Yes, that works.',
    time: 'Jun 12, 9:20 AM'
  }]
}, {
  id: 4,
  type: 'email',
  consumer: 'Amanda Cole',
  account: '102863520',
  preview: 'Payment confirmation receipt',
  time: 'Jun 10',
  unread: 0,
  subject: 'Payment confirmation receipt',
  messages: [{
    direction: 'out',
    from: 'Compass Recovery Group <noreply@compassrecoverygroup.com>',
    to: 'acole@outlook.com',
    text: 'Hi Amanda,\n\nThis email confirms receipt of your final payment of $250.00 on Jun 9, 2026. Your account is now paid in full.\n\nThank you for working with us.\n\nCompass Recovery Group',
    time: 'Jun 10, 10:00 AM'
  }, {
    direction: 'in',
    from: 'acole@outlook.com',
    to: 'noreply@compassrecoverygroup.com',
    text: 'Thank you! Appreciate the quick confirmation.',
    time: 'Jun 10, 11:14 AM'
  }]
}, {
  id: 5,
  type: 'sms',
  consumer: 'Robert Fox',
  account: '102863521',
  preview: 'STOP',
  time: 'Jun 8',
  unread: 0,
  messages: [{
    direction: 'out',
    text: 'Hi Robert, your account is past due. Please contact us to discuss options.',
    time: 'Jun 8, 8:00 AM',
    system: true
  }, {
    direction: 'in',
    text: 'STOP',
    time: 'Jun 8, 8:14 AM'
  }]
}];
export function Communications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'all' | 'sms' | 'email' | 'unread'>('all');
  const [selectedThread, setSelectedThread] = useState<number | null>(1);
  const [replyText, setReplyText] = useState('');
  const [replySubject, setReplySubject] = useState('');
  useEffect(() => {
    const channel = searchParams.get('channel');
    const consumer = searchParams.get('consumer');
    if (channel === 'sms' || channel === 'email') {
      setActiveTab(channel);
    }
    if (consumer) {
      const match = mockThreads.find(t => t.consumer.toLowerCase() === consumer.toLowerCase() && (!channel || t.type === channel));
      if (match) setSelectedThread(match.id);
    }
    if (channel || consumer) {
      const next = new URLSearchParams(searchParams);
      next.delete('channel');
      next.delete('consumer');
      setSearchParams(next, {
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const filteredThreads = mockThreads.filter(t => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return t.unread > 0;
    return t.type === activeTab;
  });
  const thread = mockThreads.find(t => t.id === selectedThread) ?? null;
  const handleTemplate = (text: string) => setReplyText(text);
  const handleAskAI = () => {
    if (!thread) return;
    if (thread.type === 'sms') {
      setReplyText(prev => prev + (prev ? ' ' : '') + `Hi ${thread.consumer.split(' ')[0]}, thanks for your message. We'll follow up shortly with next steps.`);
    } else {
      setReplyText(prev => prev + (prev ? '\n\n' : '') + `Hi ${thread.consumer.split(' ')[0]},\n\nThanks for getting in touch. We've reviewed your account and will follow up shortly with next steps.\n\nBest,\nCompass Recovery Group`);
    }
  };
  return <div className="flex h-full bg-white" data-id="element-954">
      {/* Left Pane - List */}
      <div className="w-1/3 min-w-[320px] max-w-[400px] border-r border-gray-200 flex flex-col bg-gray-50" data-id="element-955">
        <div className="p-4 border-b border-gray-200 bg-white shrink-0" data-id="element-956">
          <div className="relative mb-4" data-id="element-957">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-958" />
            <input type="text" placeholder="Search messages..." className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy focus:bg-white transition-all" data-id="element-959" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-id="element-960">
            {(['all', 'sms', 'email', 'unread'] as const).map(t => <button key={t} onClick={() => setActiveTab(t)} className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-colors ${activeTab === t ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-961">
                {t}
              </button>)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-962">
          {filteredThreads.length === 0 ? <div className="p-8 text-center text-sm text-gray-400" data-id="element-963">
              No conversations
            </div> : filteredThreads.map(t => <button key={t.id} onClick={() => setSelectedThread(t.id)} className={`w-full text-left p-4 border-b border-gray-200 transition-colors relative ${selectedThread === t.id ? 'bg-white' : 'hover:bg-gray-100/50'}`} data-id="element-964">
                {selectedThread === t.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-red" data-id="element-965"></div>}
                <div className="flex justify-between items-baseline mb-1" data-id="element-966">
                  <div className="flex items-center gap-2" data-id="element-967">
                    {t.type === 'sms' ? <MessageSquare size={14} className="text-blue-500" data-id="element-968" /> : <Mail size={14} className="text-purple-500" data-id="element-969" />}
                    <span className={`text-sm font-bold ${t.unread ? 'text-brand-navy' : 'text-gray-700'}`} data-id="element-970">
                      {t.consumer}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-971">
                    <span className={`text-xs ${t.unread ? 'text-brand-red font-bold' : 'text-gray-400 font-medium'}`} data-id="element-972">
                      {t.time}
                    </span>
                    {t.unread > 0 && <span className="w-2 h-2 rounded-full bg-brand-red" data-id="element-973"></span>}
                  </div>
                </div>
                <p className={`text-sm truncate ${t.unread ? 'text-brand-dark font-medium' : 'text-gray-500'}`} data-id="element-974">
                  {t.preview}
                </p>
              </button>)}
        </div>
      </div>

      {/* Right Pane */}
      {thread ? <div className="flex-1 flex flex-col min-w-0 bg-white" data-id="element-975">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0" data-id="element-976">
            <div data-id="element-977">
              <h3 className="text-base font-bold text-brand-navy flex items-center gap-2" data-id="element-978">
                {thread.type === 'sms' ? <MessageSquare size={18} className="text-blue-500" data-id="element-979" /> : <Mail size={18} className="text-purple-500" data-id="element-980" />}
                {thread.consumer}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-981">
                Acct #{thread.account}
              </p>
              {thread.type === 'email' && thread.subject && <p className="text-sm font-bold text-brand-dark mt-2" data-id="element-982">
                  {thread.subject}
                </p>}
            </div>
            <div className="flex items-center gap-3" data-id="element-983">
              <Link to="/portal/admin/consumers" className="flex items-center gap-1.5 text-xs font-bold text-brand-navy hover:text-brand-red transition-colors px-3 py-1.5 bg-gray-50 rounded-md border border-gray-200" data-id="element-984">
                View Profile <ExternalLink size={12} data-id="element-985" />
              </Link>
              <button className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors" data-id="element-986">
                <MoreHorizontal size={20} data-id="element-987" />
              </button>
            </div>
          </div>

          {/* Body — SMS bubble vs email message list */}
          {thread.type === 'sms' ? <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50" data-id="element-988">
              {thread.messages.map((m, i) => <div key={i} className={`flex flex-col gap-1 ${m.direction === 'out' ? 'items-end' : 'items-start'}`} data-id="element-989">
                  <div className={`px-4 py-2.5 rounded-2xl max-w-[80%] text-sm shadow-sm ${m.direction === 'out' ? 'bg-brand-navy text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-brand-dark rounded-tl-sm'}`} data-id="element-990">
                    {m.text}
                  </div>
                  <span className={`text-[11px] text-gray-400 font-medium ${m.direction === 'out' ? 'mr-1' : 'ml-1'}`} data-id="element-991">
                    {m.time}
                    {m.system ? ' • System' : ''}
                  </span>
                </div>)}
            </div> : <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/50" data-id="element-992">
              {thread.messages.map((m, i) => <article key={i} className={`bg-white border rounded-lg shadow-sm ${m.direction === 'out' ? 'border-brand-navy/20' : 'border-gray-200'}`} data-id="element-993">
                  <div className="px-5 py-3 border-b border-gray-100 flex items-start justify-between gap-4" data-id="element-994">
                    <div className="min-w-0" data-id="element-995">
                      <div className="flex items-center gap-2 mb-0.5" data-id="element-996">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${m.direction === 'out' ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-997">
                          {m.direction === 'out' ? 'Sent' : 'Received'}
                        </span>
                        <p className="text-xs font-bold text-brand-navy truncate" data-id="element-998">
                          From:{' '}
                          <span className="font-medium text-gray-700" data-id="element-999">
                            {m.from}
                          </span>
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 truncate" data-id="element-1000">
                        To:{' '}
                        <span className="font-medium text-gray-700" data-id="element-1001">
                          {m.to}
                        </span>
                      </p>
                    </div>
                    <span className="text-[11px] text-gray-400 font-medium shrink-0" data-id="element-1002">
                      {m.time}
                    </span>
                  </div>
                  <div className="px-5 py-4" data-id="element-1003">
                    <p className="text-sm text-brand-dark whitespace-pre-wrap leading-relaxed" data-id="element-1004">
                      {m.text}
                    </p>
                  </div>
                </article>)}
            </div>}

          {/* Composer */}
          <div className="p-4 border-t border-gray-200 bg-white shrink-0" data-id="element-1005">
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide" data-id="element-1006">
              <button onClick={() => handleTemplate(thread.type === 'sms' ? 'Just a friendly reminder that your payment is due.' : 'Hi,\n\nJust a friendly reminder that your payment is due.\n\nBest,\nCompass Recovery Group')} className="shrink-0 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-brand-navy text-xs font-bold rounded-full transition-colors" data-id="element-1007">
                Payment reminder
              </button>
              <button onClick={() => handleTemplate(thread.type === 'sms' ? 'Here are the details of your settlement offer:' : 'Hi,\n\nHere are the details of your settlement offer:\n\n')} className="shrink-0 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-brand-navy text-xs font-bold rounded-full transition-colors" data-id="element-1008">
                Offer details
              </button>
              <button onClick={() => handleTemplate(thread.type === 'sms' ? 'Thank you, your payment has been confirmed.' : 'Hi,\n\nThank you, your payment has been confirmed.\n\nBest,\nCompass Recovery Group')} className="shrink-0 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-brand-navy text-xs font-bold rounded-full transition-colors" data-id="element-1009">
                Confirmation
              </button>
            </div>

            {thread.type === 'email' && <div className="mb-2 flex items-center gap-2 text-xs text-gray-500" data-id="element-1010">
                <Reply size={14} className="text-gray-400" data-id="element-1011" />
                <span className="font-bold text-gray-600" data-id="element-1012">Reply to:</span>
                <span className="font-medium" data-id="element-1013">
                  {thread.messages[thread.messages.length - 1]?.from}
                </span>
              </div>}

            {thread.type === 'email' && <input type="text" value={replySubject} onChange={e => setReplySubject(e.target.value)} placeholder={`Re: ${thread.subject ?? ''}`} className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-2 text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy" data-id="element-1014" />}

            <div className="relative" data-id="element-1015">
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder={thread.type === 'sms' ? 'Write a message...' : 'Write your email...'} className="w-full border border-gray-300 rounded-lg pl-4 pr-24 py-3 text-sm focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy resize-none" rows={thread.type === 'sms' ? 3 : 6} data-id="element-1016"></textarea>
              <div className="absolute bottom-3 right-3 flex items-center gap-2" data-id="element-1017">
                {thread.type === 'email' && <button className="p-2 text-gray-400 hover:text-brand-navy hover:bg-gray-100 rounded-full transition-colors" title="Attach file" data-id="element-1018">
                    <Paperclip size={16} data-id="element-1019" />
                  </button>}
                <button onClick={handleAskAI} className="flex items-center gap-1.5 px-3 py-1.5 border border-brand-navy text-brand-navy hover:bg-brand-navy/5 rounded-full text-xs font-bold transition-colors" title="Ask AI to draft reply" data-id="element-1020">
                  <Sparkles size={14} data-id="element-1021" /> AI
                </button>
                <button className="p-2 bg-brand-red hover:bg-red-800 text-white rounded-full transition-colors shadow-sm" data-id="element-1022">
                  <Send size={16} className="-ml-0.5 mt-0.5" data-id="element-1023" />
                </button>
              </div>
            </div>
          </div>
        </div> : <div className="flex-1 flex flex-col items-center justify-center bg-gray-50" data-id="element-1024">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4" data-id="element-1025">
            <MessageSquare size={24} className="text-gray-400" data-id="element-1026" />
          </div>
          <p className="text-gray-500 font-medium" data-id="element-1027">Select a conversation</p>
        </div>}
    </div>;
}